import { useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import {
  SelectedEmployeeData,
  useGetSelectedEmployeeData,
} from '~/hooks/employee/useGetSelectedEmployeeData';
import {
  UseMyAccessToSelectedEmployeeOutput,
  useMyAccessToSelectedEmployee,
} from '~/hooks/employee/useMyAccessToSelectedEmployee';

export type UseSelectedEmployeeDetailsOutput = {
  isLoading: boolean;
  isDataInSync: boolean;
  employeeData?: SelectedEmployeeData;
} & UseMyAccessToSelectedEmployeeOutput;

export const useSelectedEmployeeDetails = (
  props: {
    employeeId?: number;
  },
  swrConfig?: SWRConfiguration,
): UseSelectedEmployeeDetailsOutput => {
  const { employeeId } = props;

  const swrDataConfig = {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    refreshInterval: 0,
    ...swrConfig,
  };

  // Resolve selected employee data
  const { data: employeeData, isLoading: isLoadingEmployeeData } =
    useGetSelectedEmployeeData(
      {
        employeeId,
      },
      swrDataConfig,
    );

  const { isDataInSync, selectedData } = useMemo(() => {
    const isDataInSync = employeeId === employeeData?.employeeId;
    const selectedData = isDataInSync ? employeeData : undefined;

    return {
      isDataInSync,
      selectedData,
    };
  }, [employeeId, employeeData]);

  // Resolve my access to selected employee
  const { isLoading: isLoadingMyAccess, ...myAccessProps } =
    useMyAccessToSelectedEmployee(
      {
        employeeId,
        unitId: selectedData?.unit,
      },
      swrDataConfig,
    );

  return {
    isLoading: isLoadingEmployeeData || isLoadingMyAccess,
    isDataInSync,
    employeeData: selectedData,
    ...myAccessProps,
  };
};
